import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Bookmark, ChevronRight, Scale, Heart, SlidersHorizontal, X } from "lucide-react";
import TopBar from "@/components/TopBar";
import Skeleton from "@/components/Skeleton";
import api, { formatApiError } from "@/lib/api";
import { useToast } from "@/components/Toast";
import { useOutfitDraft } from "@/context/OutfitDraftContext";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const opts = d.getFullYear() === now.getFullYear()
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" };
  return d.toLocaleDateString(undefined, opts);
}

const SCORE_FILTERS = [
  { id: "all",  label: "All",   min: 0 },
  { id: "8",    label: "8+",    min: 8 },
  { id: "7",    label: "7+",    min: 7 },
];

export default function SavedScreen() {
  const navigate = useNavigate();
  const toast = useToast();
  const { update, reset } = useOutfitDraft();
  const [tab, setTab] = useState("outfits");
  const [outfits, setOutfits] = useState(null);
  const [ratings, setRatings] = useState(null);
  const [error, setError] = useState("");
  const [openOutfitId, setOpenOutfitId] = useState(null);
  const [openRatingId, setOpenRatingId] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [favOnly, setFavOnly] = useState(false);
  const [scoreFilter, setScoreFilter] = useState("all");
  const [styleFilter, setStyleFilter] = useState("all");

  const load = async () => {
    try {
      const [o, r] = await Promise.all([
        api.get("/outfits/saved").then((x) => x.data).catch(() => []),
        api.get("/ratings/history").then((x) => x.data).catch(() => []),
      ]);
      setOutfits(o); setRatings(r);
    } catch (err) {
      setError(formatApiError(err));
      setOutfits([]); setRatings([]);
    }
  };

  useEffect(() => { load(); }, []);

  const styleOptions = useMemo(() => {
    const base = tab === "outfits" ? outfits : ratings;
    if (!base) return [];
    const set = new Set();
    base.forEach((x) => {
      const s = tab === "outfits" ? x.style : x.occasion;
      if (s) set.add(s);
    });
    return Array.from(set).sort();
  }, [tab, outfits, ratings]);

  const removeOutfit = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await api.delete(`/outfits/${id}`);
      setOutfits((arr) => arr.filter((o) => o.id !== id));
      toast.show("Outfit removed", "success");
    } catch (err) { toast.show(formatApiError(err), "error"); }
  };
  const removeRating = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await api.delete(`/ratings/${id}`);
      setRatings((arr) => arr.filter((r) => r.id !== id));
      toast.show("Rating removed", "success");
    } catch (err) { toast.show(formatApiError(err), "error"); }
  };

  const toggleOutfitFav = async (id, current, e) => {
    if (e) e.stopPropagation();
    const next = !current;
    setOutfits((arr) => arr.map((o) => o.id === id ? { ...o, favorite: next } : o));
    try { await api.patch(`/outfits/${id}/favorite`, { favorite: next }); }
    catch { setOutfits((arr) => arr.map((o) => o.id === id ? { ...o, favorite: current } : o)); }
  };
  const toggleRatingFav = async (id, current, e) => {
    if (e) e.stopPropagation();
    const next = !current;
    setRatings((arr) => arr.map((r) => r.id === id ? { ...r, favorite: next } : r));
    try { await api.patch(`/ratings/${id}/favorite`, { favorite: next }); }
    catch { setRatings((arr) => arr.map((r) => r.id === id ? { ...r, favorite: current } : r)); }
  };

  const regenerateFrom = (item) => {
    reset();
    update({ mode: "upload", style: item.style || "casual", imageBase64: item.thumbnail_base64 || null });
    navigate(item.thumbnail_base64 ? "/appearance" : "/style-pick");
  };

  const baseList = tab === "outfits" ? outfits : ratings;
  const filtered = useMemo(() => {
    if (!baseList) return null;
    const minScore = SCORE_FILTERS.find((f) => f.id === scoreFilter)?.min ?? 0;
    return baseList.filter((x) => {
      if (favOnly && !x.favorite) return false;
      const score = tab === "outfits"
        ? x.outfit?.scores?.overall
        : x.rating?.scores?.overall;
      if (minScore > 0 && (score == null || score < minScore)) return false;
      if (styleFilter !== "all") {
        const tag = tab === "outfits" ? x.style : x.occasion;
        if (tag !== styleFilter) return false;
      }
      return true;
    });
  }, [baseList, favOnly, scoreFilter, styleFilter, tab]);

  const renderSkeleton = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="saved-skeleton">
      {[0,1,2].map((i) => (
        <div key={i} style={{ display: "flex", gap: 14, padding: 14, border: "1px solid var(--line)", borderRadius: 18 }}>
          <Skeleton w={84} h={84} r={14} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, justifyContent: "center" }}>
            <Skeleton w="40%" h={10} /><Skeleton w="80%" h={16} /><Skeleton w="60%" h={12} />
          </div>
        </div>
      ))}
    </div>
  );

  const renderEmpty = (which) => (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20, textAlign: "center", padding: "60px 20px" }} data-testid={`saved-empty-${which}`}>
      <div className="tile-icon" style={{ width: 60, height: 60 }}>
        {which === "outfits" ? <Bookmark size={24} strokeWidth={1.6} /> : <Scale size={24} strokeWidth={1.6} />}
      </div>
      <div className="h2" style={{ fontSize: 22 }}>
        {favOnly || scoreFilter !== "all" || styleFilter !== "all"
          ? "No matches"
          : which === "outfits" ? "No outfits saved yet" : "No ratings saved yet"}
      </div>
      <p className="body" style={{ maxWidth: 280 }}>
        {favOnly || scoreFilter !== "all" || styleFilter !== "all"
          ? "Try clearing your filters."
          : which === "outfits"
            ? "Generate an outfit and tap save to build your wardrobe."
            : "Rate an outfit and tap save to build your style history."}
      </p>
      {!(favOnly || scoreFilter !== "all" || styleFilter !== "all") && (
        <button
          className="btn btn-primary"
          onClick={() => navigate(which === "outfits" ? "/home" : "/rate")}
          data-testid={`saved-cta-${which}`}
          style={{ maxWidth: 240 }}
        >
          {which === "outfits" ? "Create one now" : "Rate an outfit"}
        </button>
      )}
    </div>
  );

  return (
    <div className="screen fade-in" data-testid="saved-screen">
      <TopBar
        title="WARDROBE & HISTORY"
        right={
          <button
            className="icon-btn"
            onClick={() => setFiltersOpen((v) => !v)}
            aria-label="Filters"
            data-testid="saved-filter-toggle"
            style={{ background: filtersOpen ? "rgba(255,255,255,0.08)" : undefined }}
          >
            <SlidersHorizontal size={16} strokeWidth={1.7} />
          </button>
        }
      />
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 12 }}>YOUR HISTORY</div>
        <h2 className="h1" style={{ fontSize: 38 }}>
          Your <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>looks</em>
        </h2>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }} data-testid="saved-tabs">
        <button className={`chip ${tab === "outfits" ? "active" : ""}`} onClick={() => setTab("outfits")} data-testid="saved-tab-outfits">
          Outfits {outfits?.length ? `(${outfits.length})` : ""}
        </button>
        <button className={`chip ${tab === "ratings" ? "active" : ""}`} onClick={() => setTab("ratings")} data-testid="saved-tab-ratings">
          Ratings {ratings?.length ? `(${ratings.length})` : ""}
        </button>
      </div>

      {/* Filter bar */}
      {filtersOpen && (
        <div
          data-testid="saved-filter-bar"
          style={{
            border: "1px solid var(--line)",
            borderRadius: 16,
            padding: 14,
            display: "flex",
            flexDirection: "column",
            gap: 10,
            background: "var(--bg-elev)",
            marginBottom: 16,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div className="tiny" style={{ letterSpacing: "0.18em", textTransform: "uppercase" }}>Filters</div>
            {(favOnly || scoreFilter !== "all" || styleFilter !== "all") && (
              <button
                className="linkish tiny"
                onClick={() => { setFavOnly(false); setScoreFilter("all"); setStyleFilter("all"); }}
                data-testid="saved-filter-clear"
                style={{ textDecoration: "none", letterSpacing: "0.16em", textTransform: "uppercase" }}
              >
                Clear
              </button>
            )}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <button
              className={`chip ${favOnly ? "active" : ""}`}
              onClick={() => setFavOnly((v) => !v)}
              data-testid="saved-filter-favorites"
            >
              <Heart size={11} strokeWidth={1.9} style={{ marginRight: 6 }} /> Favorites
            </button>
            {SCORE_FILTERS.map((f) => (
              <button
                key={f.id}
                className={`chip ${scoreFilter === f.id ? "active" : ""}`}
                onClick={() => setScoreFilter(f.id)}
                data-testid={`saved-filter-score-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {styleOptions.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              <button
                className={`chip ${styleFilter === "all" ? "active" : ""}`}
                onClick={() => setStyleFilter("all")}
                data-testid="saved-filter-style-all"
              >
                All styles
              </button>
              {styleOptions.map((s) => (
                <button
                  key={s}
                  className={`chip ${styleFilter === s ? "active" : ""}`}
                  onClick={() => setStyleFilter(s)}
                  data-testid={`saved-filter-style-${s.replace(/\s+/g, "-")}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {error && <div className="error-text" data-testid="saved-error">{error}</div>}

      {baseList === null && renderSkeleton()}
      {filtered?.length === 0 && renderEmpty(tab)}

      {/* OUTFITS list */}
      {tab === "outfits" && filtered?.length > 0 && (
        <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="saved-list">
          {filtered.map((o) => {
            const overall = o.outfit?.scores?.overall;
            const title = o.outfit?.title || "Saved outfit";
            return (
              <div
                key={o.id}
                role="button"
                tabIndex={0}
                className="saved-card"
                onClick={() => setOpenOutfitId(o.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setOpenOutfitId(o.id);
                  }
                }}
                data-testid={`saved-item-${o.id}`}
                style={{ textAlign: "left", cursor: "pointer", font: "inherit", color: "inherit" }}>
                <div className="saved-thumb">
                  {o.thumbnail_base64
                    ? <img src={o.thumbnail_base64} alt="" />
                    : <Bookmark size={20} strokeWidth={1.5} color="rgba(255,255,255,0.45)" />}
                </div>
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6, justifyContent: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span className="tiny" style={{ letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)" }}>{o.style || "outfit"}</span>
                    {overall != null && (
                      <span className="score-chip numeric" data-testid={`saved-score-${o.id}`}>
                        <span className="star" /> {Number(overall).toFixed(1)}
                      </span>
                    )}
                  </div>
                  <div className="h3" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
                  <div className="tiny numeric" style={{ color: "rgba(255,255,255,0.4)" }} data-testid={`saved-date-${o.id}`}>
                    Saved {formatDate(o.created_at)}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, justifyContent: "center" }}>
                  <button
                    className="icon-btn"
                    onClick={(e) => toggleOutfitFav(o.id, !!o.favorite, e)}
                    aria-label="Favorite"
                    data-testid={`saved-fav-${o.id}`}
                    style={{ color: o.favorite ? "#ff6e8a" : undefined }}
                  >
                    <Heart size={14} strokeWidth={1.7} fill={o.favorite ? "#ff6e8a" : "none"} />
                  </button>
                  <button className="icon-btn" onClick={(e) => removeOutfit(o.id, e)} aria-label="Delete" data-testid={`saved-delete-${o.id}`}>
                    <Trash2 size={14} strokeWidth={1.7} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* RATINGS list */}
      {tab === "ratings" && filtered?.length > 0 && (
        <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="ratings-list">
          {filtered.map((r) => {
            const overall = r.rating?.scores?.overall;
            const title = r.rating?.title || "Rated outfit";
            return (
              <div
                key={r.id}
                role="button"
                tabIndex={0}
                className="saved-card"
                onClick={() => setOpenRatingId(r.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setOpenRatingId(r.id);
                  }
                }}
                data-testid={`rating-item-${r.id}`}
                style={{ textAlign: "left", cursor: "pointer", font: "inherit", color: "inherit" }}>
                <div className="saved-thumb">
                  {r.thumbnail_base64
                    ? <img src={r.thumbnail_base64} alt="" />
                    : <Scale size={20} strokeWidth={1.5} color="rgba(255,255,255,0.45)" />}
                </div>
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6, justifyContent: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span className="tiny" style={{ letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)" }}>{r.occasion || "Rated"}</span>
                    {overall != null && (
                      <span className="score-chip numeric" data-testid={`rating-score-${r.id}`}>
                        <span className="star" /> {Number(overall).toFixed(1)}
                      </span>
                    )}
                  </div>
                  <div className="h3" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
                  <div className="tiny numeric" style={{ color: "rgba(255,255,255,0.4)" }} data-testid={`rating-date-${r.id}`}>
                    Rated {formatDate(r.created_at)}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, justifyContent: "center" }}>
                  <button
                    className="icon-btn"
                    onClick={(e) => toggleRatingFav(r.id, !!r.favorite, e)}
                    aria-label="Favorite"
                    data-testid={`rating-fav-${r.id}`}
                    style={{ color: r.favorite ? "#ff6e8a" : undefined }}
                  >
                    <Heart size={14} strokeWidth={1.7} fill={r.favorite ? "#ff6e8a" : "none"} />
                  </button>
                  <button className="icon-btn" onClick={(e) => removeRating(r.id, e)} aria-label="Delete" data-testid={`rating-delete-${r.id}`}>
                    <Trash2 size={14} strokeWidth={1.7} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Outfit detail overlay */}
      {openOutfitId && outfits && (() => {
        const item = outfits.find((x) => x.id === openOutfitId);
        if (!item) return null;
        const o = item.outfit || {};
        const items = o.items || {};
        return (
          <div onClick={() => setOpenOutfitId(null)} data-testid="saved-detail-overlay"
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", backdropFilter: "blur(8px)", zIndex: 200, display: "flex", alignItems: "flex-end", justifyContent: "center", padding: 16, animation: "fadeIn 280ms ease" }}>
            <div onClick={(e) => e.stopPropagation()} data-testid="saved-detail-card"
              style={{ background: "#0a0a0a", border: "1px solid var(--line-strong)", borderRadius: 24, maxWidth: 420, width: "100%", maxHeight: "80vh", overflowY: "auto", padding: 22, display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div className="eyebrow">{(item.style || "outfit").toUpperCase()}</div>
                <button className="icon-btn" onClick={() => setOpenOutfitId(null)} aria-label="Close"><X size={16} /></button>
              </div>
              <div className="h2" style={{ fontSize: 24 }}>{o.title || "Saved outfit"}</div>
              {o.scores?.overall != null && (
                <span className="score-chip numeric" style={{ alignSelf: "flex-start" }}>
                  <span className="star" /> {Number(o.scores.overall).toFixed(1)} / 10
                </span>
              )}
              {o.summary && <p className="body-sm" style={{ color: "#fff" }}>{o.summary}</p>}
              <div className="hairline" />
              {["top","bottom","outerwear","shoes"].map((k) => {
                const it = items[k];
                if (!it?.name) return null;
                return (
                  <div key={k}>
                    <div className="tiny" style={{ letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)" }}>{k}</div>
                    <div className="h3" style={{ marginTop: 4 }}>{it.name}</div>
                    {it.description && <div className="body-sm" style={{ marginTop: 4 }}>{it.description}</div>}
                  </div>
                );
              })}
              {(items.accessories || []).map((a, i) => (
                <div key={`acc-${i}`}>
                  <div className="tiny" style={{ letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)" }}>Accessory</div>
                  <div className="h3" style={{ marginTop: 4 }}>{a.name}</div>
                  {a.description && <div className="body-sm" style={{ marginTop: 4 }}>{a.description}</div>}
                </div>
              ))}
              <div className="hairline" />
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button className="btn btn-primary" onClick={() => regenerateFrom(item)} data-testid="saved-detail-regen-btn">Re-style this look</button>
                <button className="btn btn-outline" onClick={() => setOpenOutfitId(null)} data-testid="saved-detail-close-btn">Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Rating detail overlay */}
      {openRatingId && ratings && (() => {
        const item = ratings.find((x) => x.id === openRatingId);
        if (!item) return null;
        const rating = item.rating || {};
        const scores = rating.scores || {};
        return (
          <div onClick={() => setOpenRatingId(null)} data-testid="rating-detail-overlay"
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", backdropFilter: "blur(8px)", zIndex: 200, display: "flex", alignItems: "flex-end", justifyContent: "center", padding: 16, animation: "fadeIn 280ms ease" }}>
            <div onClick={(e) => e.stopPropagation()} data-testid="rating-detail-card"
              style={{ background: "#0a0a0a", border: "1px solid var(--line-strong)", borderRadius: 24, maxWidth: 420, width: "100%", maxHeight: "85vh", overflowY: "auto", padding: 22, display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div className="eyebrow">{(item.occasion || "RATING").toUpperCase()}</div>
                <button className="icon-btn" onClick={() => setOpenRatingId(null)} aria-label="Close"><X size={16} /></button>
              </div>
              <div className="h2" style={{ fontSize: 24 }}>{rating.title || "Rated outfit"}</div>
              {scores.overall != null && (
                <span className="score-chip numeric" style={{ alignSelf: "flex-start" }}>
                  <span className="star" /> {Number(scores.overall).toFixed(1)} / 10
                </span>
              )}
              {item.thumbnail_base64 && (
                <img src={item.thumbnail_base64} alt="" className="preview-img" style={{ maxHeight: 280 }} />
              )}
              {rating.summary && <p className="body-sm" style={{ color: "#fff" }}>{rating.summary}</p>}
              <div className="hairline" />
              {Object.keys(scores).filter(k => k !== "overall").map((k) => (
                <div key={k} className="rating-row" style={{ padding: "4px 0" }}>
                  <div className="label-cell">{k.replace(/_/g, " ")}</div>
                  <div className="bar"><div className="bar-fill" style={{ "--w": `${(Number(scores[k] || 0) / 10) * 100}%` }} /></div>
                  <div className="score-cell">{Number(scores[k] || 0).toFixed(1)}</div>
                </div>
              ))}
              {(rating.strengths || []).length > 0 && (
                <>
                  <div className="eyebrow" style={{ marginTop: 8 }}>STRENGTHS</div>
                  <ul className="bullet-list">{rating.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
                </>
              )}
              {(rating.suggestions || []).length > 0 && (
                <>
                  <div className="eyebrow" style={{ marginTop: 8 }}>SUGGESTIONS</div>
                  <ul className="bullet-list">{rating.suggestions.map((s, i) => <li key={i}>{s}</li>)}</ul>
                </>
              )}
              {rating.improvement_plan && (
                <>
                  <div className="hairline" style={{ margin: "8px 0" }} />
                  <div className="eyebrow">HOW TO IMPROVE</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 14, padding: 14, border: "1px solid var(--line-strong)", borderRadius: 14, background: "rgba(255,255,255,0.02)" }}>
                    <div style={{ flex: 1, textAlign: "center" }}>
                      <div className="tiny" style={{ letterSpacing: "0.16em", textTransform: "uppercase" }}>Current</div>
                      <div className="h3 numeric" style={{ marginTop: 4, color: "rgba(255,255,255,0.7)" }}>
                        {Number(rating.improvement_plan.current_score ?? scores.overall ?? 0).toFixed(1)}
                      </div>
                    </div>
                    <div style={{ color: "var(--text-mute)", fontSize: 18 }}>→</div>
                    <div style={{ flex: 1, textAlign: "center" }}>
                      <div className="tiny" style={{ letterSpacing: "0.16em", textTransform: "uppercase" }}>Improve to</div>
                      <div className="h3 numeric" style={{ marginTop: 4, color: "#fff" }}>
                        {Number(rating.improvement_plan.improved_score ?? 0).toFixed(1)}
                      </div>
                    </div>
                  </div>
                  {(rating.improvement_plan.specific_changes || []).length > 0 && (
                    <ul className="bullet-list">
                      {rating.improvement_plan.specific_changes.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  )}
                </>
              )}
              <div className="hairline" />
              <button className="btn btn-outline" onClick={() => setOpenRatingId(null)} data-testid="rating-detail-close-btn">Close</button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
